import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { fetchGuestBookings } from '../../api/guestApi';
import { listGuestInvoices } from '../../api/invoices';

const StatCard = ({ label, value, tone }) => {
  const styles = {
    slate: 'bg-slate-50 border-slate-200 text-slate-800',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    amber: 'bg-amber-50 border-amber-100 text-amber-700',
    sky: 'bg-sky-50 border-sky-100 text-sky-700',
  };
  return (
    <div className={`rounded-2xl border px-4 py-4 shadow-sm ${styles[tone] || styles.slate}`}>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-2 text-2xl font-semibold">{value}</p>
    </div>
  );
};

const canPayBooking = (booking) => {
  const bookingStatus = String(booking?.booking_status || '').toLowerCase();
  const paymentStatus = String(booking?.payment_status || '').toLowerCase();
  if (['cancelled', 'cancel_requested', 'declined', 'expired', 'refunded', 'completed'].includes(bookingStatus)) {
    return false;
  }
  return ['pending', 'requires_payment', 'unpaid', 'failed'].includes(paymentStatus);
};

const GuestDashboard = () => {
  const { summary } = useOutletContext();
  const [bookings, setBookings] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await fetchGuestBookings({ limit: 5 });
        setBookings(res.data || []);
      } catch (err) {
        setError(err.message || 'Unable to load bookings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const loadInvoices = async () => {
      setInvoiceLoading(true);
      setInvoiceError('');
      try {
        const res = await listGuestInvoices({ limit: 4 });
        setInvoices(res.data || []);
      } catch (err) {
        setInvoiceError(err.message || 'Unable to load invoices');
      } finally {
        setInvoiceLoading(false);
      }
    };
    loadInvoices();
  }, []);

  const recentBookings = useMemo(() => bookings.slice(0, 4), [bookings]);
  const recentInvoices = useMemo(() => invoices.slice(0, 4), [invoices]);

  const formatDate = (value) => {
    if (!value) return 'TBD';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString();
  };

  const formatAmount = (currency, amount) => {
    const numeric = Number(amount);
    if (!Number.isFinite(numeric)) return `${currency || ''} ${amount || ''}`.trim();
    return `${currency || ''} ${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`.trim();
  };

  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total bookings" value={summary?.totalBookings || 0} tone="slate" />
        <StatCard label="Upcoming stays" value={summary?.upcomingStays || 0} tone="emerald" />
        <StatCard label="Pending payments" value={summary?.pendingPayments || 0} tone="amber" />
        <StatCard label="Unread messages" value={summary?.unreadMessages || 0} tone="sky" />
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Recent bookings</p>
            <p className="text-xs text-slate-500">Track what is coming up next.</p>
          </div>
          <span className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">
            {summary?.totalBookings || 0} total
          </span>
        </div>

        {loading && <div className="p-4 text-sm text-slate-500">Loading bookings...</div>}
        {error && <div className="p-4 text-sm text-rose-600">{error}</div>}
        {!loading && !error && recentBookings.length === 0 && (
          <div className="p-4 text-sm text-slate-500">No bookings yet.</div>
        )}
        {!loading && !error && recentBookings.length > 0 && (
          <div className="divide-y divide-slate-100">
            {recentBookings.map((b) => (
              <div key={b.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-slate-900">{b.booking_code}</p>
                  <p className="text-xs text-slate-500">
                    {formatDate(b.check_in_date || b.travel_start_date)} - {b.payment_status || 'pending'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                    {b.booking_status || 'confirmed'}
                  </span>
                  <Link
                    to="/guest/bookings"
                    className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-600 hover:border-emerald-200 hover:text-emerald-700"
                  >
                    Manage
                  </Link>
                  {canPayBooking(b) && (
                    <Link
                      to={`/booking/payment?bookingId=${encodeURIComponent(b.booking_code || b.id)}`}
                      className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700"
                    >
                      Pay now
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-slate-900">Recent invoices</p>
            <p className="text-xs text-slate-500">Your latest tax invoices and receipts.</p>
          </div>
          <Link
            to="/guest/invoices"
            className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700"
          >
            View all
          </Link>
        </div>

        {invoiceLoading && <div className="p-4 text-sm text-slate-500">Loading invoices...</div>}
        {invoiceError && <div className="p-4 text-sm text-rose-600">{invoiceError}</div>}
        {!invoiceLoading && !invoiceError && recentInvoices.length === 0 && (
          <div className="p-4 text-sm text-slate-500">No invoices yet.</div>
        )}
        {!invoiceLoading && !invoiceError && recentInvoices.length > 0 && (
          <div className="divide-y divide-slate-100">
            {recentInvoices.map((inv) => {
              const total = inv.totals?.grandTotal ?? inv.totals?.total ?? 0;
              return (
                <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{inv.invoiceNumber}</p>
                    <p className="text-xs text-slate-500">
                      Booking {inv.stayDetails?.bookingCode || inv.bookingId} · {formatDate(inv.issuedAt)}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-slate-900">{formatAmount(inv.currency, total)}</p>
                    <p className="text-xs text-slate-500">{String(inv.status || 'issued').replace(/_/g, ' ')}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GuestDashboard;
